import type { paths } from '@connected/api-client/api.generated';
import type { UseMutationOptions } from '@tanstack/react-query';
import type { FetchResponse, MaybeOptionalInit } from 'openapi-fetch';
import type { MediaType } from 'openapi-typescript-helpers';

import { api } from '@connected/api-client';
import { useCallback, useMemo, useState } from 'react';

type Variables<TPath extends AvailableURL> = MaybeOptionalInit<
    paths[TPath],
    'patch'
>;
type Operation<TPath extends AvailableURL> = paths[TPath]['put'];

type Data<TPath extends AvailableURL> = Required<
    FetchResponse<Operation<TPath>, Variables<TPath>, MediaType>
>['data'];

type Error<TPath extends AvailableURL> = Required<
    FetchResponse<Operation<TPath>, Variables<TPath>, MediaType>
>['error'];

type MutationOptions<TPath extends AvailableURL> = Omit<
    UseMutationOptions<
        Data<TPath>,
        Error<TPath>,
        Variables<TPath>
    >,
    'mutationFn' | 'mutationKey'
>;

export type Option<TValue> = {
    label: string;
    value: TValue;
};

export type OptionDiff<TValue> = {
    added: Option<TValue>[];
    removed: Option<TValue>[];
};

export type UseSelectableOptions<TValue> = {
    options: Option<TValue>[];
    selectedValues: TValue[];
    onChange: (values: TValue[]) => boolean | string;
};

const selectItems = {
    DocumentTemplate: {
        url: '/api/v2/electrical/projects/documents/templates/edit',
        id: 'ElectricalProjectDocumentTemplateId',
        valuesKey: 'Templates',
    },
    InspectionExclusion: {
        url: '/api/v2/electrical/projects/selectlists/items/edit',
        id: 'ElectricalProjectSelectListItemId',
    },
    ObjectSpecialSpace: {
        url: '/api/v2/electrical/projects/selectlists/items/edit',
        id: 'ElectricalProjectSelectListItemId',
    },
    AdditionalInstruction: {
        url: '/api/v2/electrical/projects/selectlists/items/edit',
        id: 'ElectricalProjectSelectListItemId',
    },
    AdditionalTopic: {
        url: '/api/v2/electrical/projects/selectlists/items/edit',
        id: 'ElectricalProjectSelectListItemId',
    },
    InspectionComponent: {
        url: '/api/v2/electrical/projects/selectlists/items/edit',
        id: 'ElectricalProjectSelectListItemId',
    },
    BuildingFunction: {
        url: '/api/v2/electrical/projects/selectlists/items/edit',
        id: 'ElectricalProjectSelectListItemId',
    },
} as const;

type SelectItems = typeof selectItems;
type AvailableURL = SelectItems[keyof SelectItems]['url'];

export type SelectItemsTypes = keyof typeof selectItems;

export function useSelectableOptions<TSelectItemsTypes extends SelectItemsTypes, TValue extends string>({
    options,
    initialSelectedValues = [],
    selectItemType,
    electricalProjectVersionId,
    scopeIdentifiers,
    ...mutationOptions
}: {
    options: Option<TValue>[];
    initialSelectedValues?: TValue[];
    selectItemType: TSelectItemsTypes;
    electricalProjectVersionId: string;
    /**
     * @deprecated This parameter is no longer used.
     */
    parentKind?: string;
    scopeIdentifiers?: Record<string, unknown>;
} & MutationOptions<SelectItems[TSelectItemsTypes]['url']>): UseSelectableOptions<TValue> {
    const [selectedValues, setSelectedValues] = useState<TValue[]>(
        initialSelectedValues,
    );

    const selectItemMutation = api.useMutation(
        'put',
        selectItems[selectItemType].url,
        // @ts-expect-error: Typescript fix
        {
            scope: {
                id: electricalProjectVersionId,
            },
            ...mutationOptions,
        },
    );

    const onChange = useCallback(
        (values: TValue[]) => {
            const itemsToUpdate: {
                id: TValue;
                selected: boolean;
            }[] = [];

            const addedItems = values.filter(
                value => !selectedValues.includes(value),
            );
            const removedItems = selectedValues.filter(
                value => !values.includes(value),
            );

            addedItems.forEach((item) => {
                setSelectedValues(prev => [...prev, item]);
                itemsToUpdate.push({ id: item, selected: true });
            });

            removedItems.forEach((item) => {
                setSelectedValues(prev =>
                    prev.filter(prevItem => prevItem !== item),
                );
                itemsToUpdate.push({ id: item, selected: false });
            });

            const body = {
                ElectricalProjectVersionId: electricalProjectVersionId,
            };

            // HACK: Typescript fix
            type ItemPayload = {
                [key: string]: TValue | boolean;
                Selected: boolean;
            };

            type BodyType = {
                ElectricalProjectVersionId: string;
                [key: string]: any;
            };

            const config = selectItems[selectItemType];
            const mappedItems = itemsToUpdate.map(item => ({
                [config.id]: item.id,
                Selected: item.selected,
            }) as ItemPayload);

            if ('valuesKey' in config) {
                (body as BodyType)[config.valuesKey] = mappedItems;
            }
            else {
                (body as BodyType).Items = mappedItems;
                (body as BodyType).ListKind = selectItemType;
            }

            selectItemMutation.mutate({
                body,
            });

            return selectItemMutation.isError
                ? selectItemMutation.error.Message || false
                : true;
        },
        [
            electricalProjectVersionId,
            selectItemMutation,
            selectItemType,
            selectedValues,
        ],
    );

    return useMemo(
        () => ({
            options,
            selectedValues,
            onChange,
        }),
        [onChange, options, selectedValues],
    );
}
